import { useState } from 'react';
import { Layout } from '../components/Layout';
import { useEditor } from '../hooks/useEditor';
import { RewriteHistoryPanel } from '../components/RewriteHistoryPanel';
import { TemplateSelector } from '../components/TemplateSelector';
import { ContextSelectors } from '../components/ContextSelectors';
import { RawInputEditor } from '../components/RawInputEditor';
import { UsageCounter } from '../components/UsageCounter';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { LimitReachedState } from '../components/LimitReachedState';
import { OutputPanel } from '../components/OutputPanel';
import { createCheckoutSession } from '../services/api';
import { useUserState } from '../hooks/useUserState';
import type { HistoryItem } from '../types';

function Editor() {
  const { tier, usageRemaining, setUsageRemaining, isLoading: tierLoading } =
    useUserState();
  const editor = useEditor(setUsageRemaining);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);

  function handleEditOriginal(item: HistoryItem) {
    editor.setRawInput(item.raw_input);
    editor.setTemplate(item.template);
    editor.setAudience(item.audience);
    editor.setTone(item.tone);
    editor.setChannel(item.channel);
    editor.reset();
    setHistoryPanelOpen(false);
  }

  return (
    <Layout mode="sidebar" tier={tier}>
      <div style={{
        flex: 1,
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <div style={{ padding: '32px 24px', maxWidth: '1000px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '28px',
      }}>
        <div>
          <h1 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 'clamp(22px, 4vw, 30px)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--text-primary)',
            marginBottom: '6px',
          }}>
            Executive Polish
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Transform any draft into high-impact executive communication.
          </p>
        </div>
        <button
          onClick={() => setHistoryPanelOpen(p => !p)}
          title="Rewrite history"
          style={{
            background: historyPanelOpen ? 'var(--accent-bg)' : 'transparent',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '6px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: historyPanelOpen ? 'var(--accent)' : 'var(--text-secondary)',
            fontSize: '13px',
            fontWeight: 500,
            transition: 'all 0.2s',
            flexShrink: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round"
               strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          History
        </button>
      </div>

      {/* Template section */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--text-muted)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: '10px',
          fontFamily: "'Outfit', sans-serif",
        }}>
          Template
        </p>
        <TemplateSelector
          value={editor.template}
          onChange={editor.setTemplate} />
      </div>

      {/* Context selectors */}
      <ContextSelectors
        audience={editor.audience}
        tone={editor.tone}
        channel={editor.channel}
        onAudienceChange={editor.setAudience}
        onToneChange={editor.setTone}
        onChannelChange={editor.setChannel} />

      {/* Input */}
      <RawInputEditor
        value={editor.rawInput}
        onChange={editor.setRawInput}
        maxChars={editor.MAX_CHARS}
        disabled={editor.editorState.status === 'loading'} />

      {/* Submit row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <UsageCounter
          rewritesRemaining={usageRemaining}
          onUpgradeClick={async () => {
            try {
              const url = await createCheckoutSession();
              window.open(url, '_blank', 'noopener,noreferrer');
            } catch {
              alert('Unable to start checkout. Please try again.');
            }
          }} />
        <button
          onClick={editor.submit}
          disabled={!editor.rawInput.trim() || editor.editorState.status === 'loading'}
          style={{
            padding: '10px 28px',
            borderRadius: '10px',
            border: 'none',
            background: (!editor.rawInput.trim() || editor.editorState.status === 'loading')
              ? 'rgba(0,212,255,0.3)' : 'var(--accent)',
            color: 'var(--btn-primary-text)',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: "'Outfit', sans-serif",
            cursor: (!editor.rawInput.trim() || editor.editorState.status === 'loading')
              ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            boxShadow: (!editor.rawInput.trim() || editor.editorState.status === 'loading')
              ? 'none' : '0 4px 15px rgba(0,212,255,0.25)',
            whiteSpace: 'nowrap',
          }}
        >
          Rewrite
        </button>
      </div>

      {/* States */}
      {editor.editorState.status === 'loading' && <LoadingState />}
      {editor.editorState.status === 'error' && (
        <ErrorState
          message={editor.editorState.message}
          onRetry={editor.submit}
          onClear={editor.reset} />
      )}
      {editor.editorState.status === 'input_error' && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          background: 'var(--error-bg)',
          border: '1px solid var(--error-border)',
          borderRadius: '8px',
        }}>
          <p style={{ fontSize: '13px', color: 'var(--error-text)', margin: 0 }}>
            {editor.editorState.message}
          </p>
        </div>
      )}
      {editor.editorState.status === 'limit_reached' && <LimitReachedState />}
      {editor.editorState.status === 'success' && (
        <OutputPanel
          response={editor.editorState.response}
          template={editor.template}
          audience={editor.audience}
          tone={editor.tone}
          channel={editor.channel}
          onReset={editor.reset} />
      )}
      </div>
      </div>
      {historyPanelOpen && (
        <RewriteHistoryPanel
          tier={tierLoading ? 'free' : tier}
          onClose={() => setHistoryPanelOpen(false)}
          onEditOriginal={handleEditOriginal}
        />
      )}
      </div>
    </Layout>
  );
}

export default Editor;
